import { DateOnly, DateOnlyManipulateType, durationFormatter, numberFormatter } from '@nx-superprep/utils'
import { ChartConfiguration } from 'chart.js'
import { PerformanceStatName, PerformanceStatNameEnum } from './performance-stats'
import { RangedModuleStats, RangedOverallStats, UserStatsRange } from '../../back-end'
import { isEqual, sortBy } from 'lodash-es'

export enum PerformanceChartNameEnum {
  week = 'week',
  month = 'month',
  year = 'year'
}
export type PerformanceChartName = `${PerformanceChartNameEnum}`
export type PerformanceChartRecord = {
  readonly range:PerformanceChartName,
  readonly charts:PerformanceCharts[]
}

export interface ChartEntry {
  readonly name: PerformanceChartName;
  readonly step: DateOnlyManipulateType;
  readonly count: number;
  readonly entries: (RangedModuleStats | RangedOverallStats)[];
  readonly path: UserStatsRange;
}

export interface DataSet {
  label: string;
  data: number[];
  stack?: string;
  backgroundColor: string | string[];
  hoverBackgroundColor: string | string[];
}

export interface PerformanceCharts {
  readonly type: 'bar';
  hidden?: boolean;
  name?: PerformanceChartName;
  title: string;
  legend: boolean;
  plugins: [];
  options: ChartConfiguration<'bar'>['options'];
  readonly data: {
    labels: string[];
    datasets: DataSet[];
  };
}

export const rangeToStatRange = (range:PerformanceChartName) => {
  switch(range) {
    case PerformanceChartNameEnum.week: return UserStatsRange.Daily
    case PerformanceChartNameEnum.month: return UserStatsRange.Monthly
    case PerformanceChartNameEnum.year: return UserStatsRange.Yearly
    default: return UserStatsRange.Daily
  }
}

const _defaultScaleCallBack = (value: number|string, index: number) => {
  const v = Number(value)||0
  const result = v > 0 && index % 5 === 0 ? numberFormatter(v, 0, false) : ''
  return result
}
const _percentScaleCallback = (value: number|string, index: number) => {
  const v = Number(value)||0
  return v > 0 && index % 5 === 0 ? (100 * v).toFixed() + '%' : ''
}
const _durationScaleCallback = (value: number|string, index: number) => {
  const v = Number(value)||0
  const result = v > 0 && index % 3 === 0 ? durationFormatter(v, {single:false}) : ''
  return result != '0s' ? result : ''
}

export const _defaultScales = (callback = _defaultScaleCallBack) => ({
  x: { grid: { display: false }, stacked: true },
  y: {
    grid: { display: false },
    stacked: true,
    ticks: {
      major: { enabled: true, },
      callback,
    },
  },
})

export const _defaultChartOptions = (name: PerformanceStatName): ChartConfiguration<'bar'>['options'] => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    tooltip: {
      callbacks: {
        label: (context): string => {
          let label = context.dataset.label || ''
          if (context.parsed.y !== null) {
            const y = Number(context.parsed.y)||0
            switch (label) {
              case 'time':
                label += ': ' + durationFormatter(y)
                break
              case 'retention':
                label += ': ' + (100 * y).toFixed() + '%'
                break
              default:
                label += ': ' + numberFormatter(context.parsed.y)
                break
            }
          }
          return label
        },
      },
    }
  },
  layout: {
    padding: {
      left: 0,
      right: 20,
      top: 32,
      bottom: 0,
    },
  },
  elements: {
    bar: {
      borderWidth: 0,
      borderRadius: 0,
      borderSkipped: 'start',
    },
  },
  scales: ((type) => {
    switch(type) {
      case 'retention': return _defaultScales(_percentScaleCallback)
      case 'studied': return _defaultScales(_durationScaleCallback)
      default: return _defaultScales()
    }})(name)
})

export const _defaultData = (range: string, name: string, fill = false) => ({
  labels: ((n) => {
    switch(n) {
      case 'week': return Array(7).fill(1).map((_element, index) => { const date = new Date(); date.setDate(index); return date.toLocaleString('default', { weekday: 'short' }); })
      case 'month': return Array(new Date(new Date().getFullYear(),new Date().getMonth(),0).getDate()).fill(1).map((_element, index) => { const date = new Date(); date.setDate(index); return date.toLocaleString('default', { day: '2-digit' }); }).sort()
      case 'year': return Array(12).fill(1).map((_element, index) => { const date = new Date(); date.setMonth(index, 1); return date.toLocaleString('default', { month: 'long' }); })
      default: return
    }
  })(range),
  datasets: ((n) => {switch(n) {
    case 'retention': return [
      {
        label: 'retention',
        data: fill ? Array(7).fill(1).map(() => Math.random()) : [],
        backgroundColor: ['rgba(91,212,121, 0.5)'],
        hoverBackgroundColor: ['rgba(91,212,121, 1.0)'],
      }
    ]
    case 'correct': return [
      {
        label: 'correct',
        stack: 'stack 0',
        data: fill ? Array(7).fill(1).map(() => Math.floor(100 * Math.random())) : [],
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        hoverBackgroundColor: 'rgba(255, 99, 132, 1.0)',
      },
      {
        label: 'wrong',
        stack: 'stack 0',
        data: fill ? Array(7).fill(1).map(() => Math.floor(100 * Math.random())) : [],
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        hoverBackgroundColor: 'rgba(54, 162, 235, 1.0)',
      },
    ]
    case 'studied': return [{
      label: 'time',
      data: fill ? Array(7).fill(1).map(() => Math.floor(100000 * Math.random())) : [],
      backgroundColor: ['rgba(74,218,231, 0.5)'],
      hoverBackgroundColor: ['rgba(74,218,231, 1.0)'],
    }]
    default: return []
  }})(name),
})

export const _defaultCharts = (rangeName?:string, fill = false) => (
  ([
    PerformanceChartNameEnum.week,
    PerformanceChartNameEnum.month,
    PerformanceChartNameEnum.year,
  ].filter(r => !rangeName || `${r}` === rangeName)).map((range: PerformanceChartName) => ({
    range,
    charts: ([
      PerformanceStatNameEnum.retention,
      PerformanceStatNameEnum.correct,
      PerformanceStatNameEnum.studied
    ].map((name: PerformanceStatName) => ({
      name,
      title: ((n) => {switch(n) {
        case 'retention': return 'Retention Rate'
        case 'correct': return 'Correct Answer'
        case 'studied': return 'Time Studied'
        default: return name
      }})(name),
      type: 'bar',
      legend: false,
      plugins: [],
      options: _defaultChartOptions(name),
      data: _defaultData(range, name, fill),
    })
    )) as unknown as PerformanceCharts[]
  }))
)

export const _defaultChartEntry = (moduleId: string|undefined, range: UserStatsRange, date: DateOnly = new DateOnly()) =>   {
  const name = (r => {switch(r) {
    case UserStatsRange.Daily: return 'week'
    case UserStatsRange.Monthly: return 'month'
    case UserStatsRange.Yearly: return 'year'
  }})(range)
  const isDayStep = (['week','month'].includes(name))
  const s: ChartEntry = {
    name,
    step: isDayStep ? 'day' : 'month',
    path: isDayStep ? UserStatsRange.Daily : UserStatsRange.Monthly,
    count: name === 'week' ? 7 : name === 'month' ? date.subtract(1, 'month').daysInMonth() : 12,
    entries: []
  }
  const start = date.add(1, s.step).startOf(s.step).subtract(s.count, s.step)
  //const end = date.endOf(s.name)
  //s.count = 1 + end.diff(start, s.step)

  for (let index = 0; index < s.count; index++) {
    const _date = start.add(index, s.step)
    const _defaultModuleStats = moduleId  && moduleId.toLowerCase() !== 'overall'
    ? new RangedModuleStats(moduleId, s.path, _date.toTimestamp())
    : new RangedOverallStats(s.path, _date.toTimestamp())
    s.entries.push(_defaultModuleStats)
  }
  return s
}

export const isSmaeChartEntry = (a: ChartEntry[], b: ChartEntry[]): boolean => {
  const result = (
    a === b || (
      a.length === b.length &&
      a.every((_chartEntry) =>
        !!b.find((_bEntry) => (
          _bEntry.name === _chartEntry.name &&
          _bEntry.step === _chartEntry.step &&
          _bEntry.path === _chartEntry.path &&
          isEqual(sortBy(_bEntry.entries, (e) => e.id), sortBy(_chartEntry.entries, (e) => e.id))
        ))
      )
    )
  )
  return result
}

export const isSameDataSet = (a: DataSet[], b: DataSet[]) => {
  return (
    a === b || (
      a.length === b.length &&
      a.every((_dataset) => {
        const _bDataset = b.find(
          (_bDataset) => _bDataset.label === _dataset.label
        )
        return (
          !!_bDataset &&
          _bDataset.data.every((_data, idx) => _data === _dataset.data[idx])
        )
      })
    )
  )
}

export const isSamePerformanceChartRecord = (a:PerformanceChartRecord, b:PerformanceChartRecord): boolean => {
  return (
    a === b || (
      a.range === b.range &&
      a.charts.every((chart) =>
        !!b.charts.find(
          (c) =>
            c.name === chart.name &&
            isSameDataSet(c.data.datasets, chart.data.datasets)
        )
      )
    )
  )
}

export const isSamePerformanceChartRecordArray = (a:PerformanceChartRecord[], b:PerformanceChartRecord[]): boolean => {
  return (
    a === b || (
      a.length === b.length &&
      a.every((record) =>
        !!b.find(
          (r) =>
            r.range === record.range && isSamePerformanceChartRecord(r, record)
        )
      )
    )
  )
 }
